
















































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { firestore, storage } from "../../firebaseInit";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

/**
 * Web3Forms delivers this form to Jessie's inbox. The key is public by design:
 * it only permits sending to the addresses verified against the account that
 * owns it, so it can't be abused to mail anyone else.
 *
 * Which of those addresses it actually goes to is not set here — the submit API
 * has no recipient parameter, and any `to` field sent with the payload would be
 * silently forwarded into the body of the email as ordinary form data. The
 * recipient lives in the Web3Forms dashboard, under this form's settings.
 */
const WEB3FORMS_ACCESS_KEY = "6029e6e4-bca9-43c0-91fb-52197b639229";

@Component({ components: {} })
export default class Contact extends Vue {
  private name: string = "";
  private email: string = "";
  private message: string = "";
  private contactText: string = "";
  private status: "idle" | "sending" | "sent" | "error" = "idle";
  private showErrors: boolean = false;
  /** Honeypot: must stay false. Anything that sets it is a bot. */
  private botcheck: boolean = false;
  /** Shown on the success screen so the visitor knows where the reply goes. */
  private sentToEmail: string = "";
  /** Set when arriving from a piece's "Ask about this piece" button. */
  private aboutPiece: string = "";
  private pieceId: string = "";
  private pieceUrl: string = "";
  private pieceDescription: string = "";
  private soldPiece: boolean = false;
  private contentDoc = doc(firestore, "page-content", "SSLO9vxO73B5pvjLeUAG");

  async created() {
    await this.setUpEnquiry();

    const docSnap = await getDoc(this.contentDoc);

    if (docSnap.exists()) {
      const content: any = docSnap.data();
      this.contactText = content.contact;
    }
  }

  /**
   * Someone can enquire about one piece, keep browsing, and enquire about
   * another without this component ever unmounting. Without this the second
   * enquiry would open carrying the first one's message, or a stale
   * confirmation for a message they already sent.
   */
  @Watch("$route.query")
  onQueryChange() {
    this.resetForm();
    this.setUpEnquiry();
  }

  async setUpEnquiry() {
    this.readEnquiryQuery();
    if (!this.aboutPiece) return;
    await this.loadPiece();
    this.message = this.defaultMessage;
  }

  /** Reads `?about=`, `?piece=` and `?sold=` left by the art detail page. */
  readEnquiryQuery() {
    const about = this.$route.query.about;
    const id = this.$route.query.piece;
    this.soldPiece = this.$route.query.sold === "1";
    // Taken from the URL rather than waiting for the record, so the space for
    // the artwork is reserved on the first paint. Fetching it is a round-trip
    // to Firestore and then another to Storage; laying the page out twice in
    // between is what moves the form out from under someone's thumb.
    this.pieceId = typeof id === "string" ? id : "";
    // The name from the URL is only a fallback for the record's own title, so
    // cap it: it ends up in the subject line of an email to Jessie.
    this.aboutPiece = typeof about === "string" ? about.trim().slice(0, 80) : "";
  }

  /**
   * Fetches the piece so the visitor can still see what they're writing about,
   * and replaces the name from the URL with the one on the record.
   */
  async loadPiece() {
    if (!this.pieceId) return;

    const snap = await getDoc(doc(firestore, "home-images", this.pieceId));
    if (!snap.exists()) {
      // Rather than leave a link to a piece that isn't there.
      this.pieceId = "";
      return;
    }

    const piece: any = snap.data();
    this.aboutPiece = piece.name || this.aboutPiece;
    this.pieceDescription = piece.description || "";

    if (piece.storageLocation) {
      this.pieceUrl = await getDownloadURL(
        storageRef(storage, piece.storageLocation)
      ).catch(() => "");
    }
  }

  get defaultMessage() {
    return this.soldPiece
      ? `Hi Jessie,\n\nI saw that "${this.aboutPiece}" has sold. Do you have anything similar available?\n\n`
      : `Hi Jessie,\n\nI'd like to ask about "${this.aboutPiece}".\n\n`;
  }

  get emailIsValid() {
    // Deliberately loose. The only real test of an address is delivery, and
    // strict patterns reject valid addresses more often than they catch typos.
    return /^\S+@\S+\.\S+$/.test(this.email.trim());
  }

  get nameIsInvalid() {
    return this.showErrors && !this.name.trim();
  }

  get emailIsInvalid() {
    return this.showErrors && !this.emailIsValid;
  }

  get messageIsInvalid() {
    return this.showErrors && !this.message.trim();
  }

  get formIsValid() {
    return !!this.name.trim() && this.emailIsValid && !!this.message.trim();
  }

  /**
   * Announced to screen readers, which otherwise get no hint that anything
   * happened when the form is submitted or refuses to submit.
   */
  get liveMessage() {
    if (this.status === "sending") return "Sending your message.";
    if (this.showErrors && !this.formIsValid)
      return "Your message wasn't sent. Please check the highlighted fields.";
    return "";
  }

  resetForm() {
    this.name = "";
    this.email = "";
    this.message = "";
    this.status = "idle";
    this.showErrors = false;
    this.pieceId = "";
    this.pieceUrl = "";
    this.pieceDescription = "";
  }

  /** Returns to a blank form after a send, for a second enquiry. */
  startAnother() {
    this.status = "idle";
    this.showErrors = false;
    this.message = this.aboutPiece ? this.defaultMessage : "";
    this.$nextTick(() => (this.$refs.nameInput as HTMLElement)?.focus());
  }

  focusFirstInvalid() {
    const target = this.nameIsInvalid
      ? this.$refs.nameInput
      : this.emailIsInvalid
      ? this.$refs.emailInput
      : this.$refs.messageInput;
    (target as HTMLElement)?.focus();
  }

  async handleSubmitForm() {
    if (this.status === "sending") return;

    this.showErrors = true;
    if (!this.formIsValid) {
      this.$nextTick(() => this.focusFirstInvalid());
      return;
    }

    this.status = "sending";

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: this.aboutPiece
            ? `Jessie Tucker Art — enquiry about "${this.aboutPiece}"`
            : `Jessie Tucker Art — message from ${this.name.trim()}`,
          from_name: "Jessie Tucker Art",
          name: this.name.trim(),
          email: this.email.trim(),
          message: this.message.trim(),
          ...(this.aboutPiece ? { artwork: this.aboutPiece } : {}),
          botcheck: this.botcheck,
        }),
      });

      const result = await response.json();

      // Web3Forms reports rejections in the body, so a 200 alone isn't enough.
      if (!response.ok || !result.success) throw new Error(result.message);

      this.sentToEmail = this.email.trim();
      this.status = "sent";
      this.name = "";
      this.email = "";
      this.message = "";
      this.showErrors = false;
      // The form has just been removed from the page, so focus would otherwise
      // land on nothing at all.
      this.$nextTick(() => (this.$refs.successAlert as HTMLElement)?.focus());
    } catch (error) {
      // Keep what they typed: making someone rewrite a message after a failure
      // is how you lose the enquiry entirely.
      this.status = "error";
    }
  }
}
